import React, { useState } from 'react';
import { ExternalLink, X, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PriceBoxplot = ({ stats }) => {
  const { userProfile } = useAuth();

  // Debug: verificar userProfile
  //console.log('👤 UserProfile en PriceBoxplot:', userProfile);
  const [hoveredVehicle, setHoveredVehicle] = useState(null);
  const [pinnedVehicle, setPinnedVehicle] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);

  if (!stats) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Calcular posiciones para el boxplot visual
  const totalRange = stats.max - stats.min;
  const q1Width = ((stats.q1 - stats.min) / totalRange) * 100;
  const medianWidth = ((stats.median - stats.min) / totalRange) * 100;
  const q3Width = ((stats.q3 - stats.min) / totalRange) * 100;

  // Umbral de cercanía en score normalizado [0,1]
  const SCORE_THRESHOLD = 0.010;

  // Calcular posiciones de vehículos individuales
  let vehicles = stats.vehicles || [];



  const vehicleMarkers = vehicles.map((vehicle) => {
    const raw01 = totalRange > 0 ? (vehicle.price - stats.min) / totalRange : 0; // [0,1]
    const rawPercent = raw01 * 100;
    const clamped = Math.max(0, Math.min(100, rawPercent));
    // Verificar si el vehículo pertenece a la agencia del usuario
    const isUserAgencyVehicle = userProfile?.agency_id &&
      vehicle.cartracker_agency_id &&
      String(vehicle.cartracker_agency_id) === String(userProfile.agency_id);

    // Debug temporal - mostrar TODOS los vehículos
    /*console.log('🔍 Vehículo:', {
      id: vehicle.id,
      title: vehicle.title?.substring(0, 40),
      price: vehicle.price,
      cartracker_agency_id: vehicle.cartracker_agency_id,
      userAgencyId: userProfile?.agency_id,
      isMatch: isUserAgencyVehicle
    });*/

    return {
      ...vehicle,
      position: clamped,
      score: Math.max(0, Math.min(1, raw01)),
      isUserAgencyVehicle: isUserAgencyVehicle
    };
  }).sort((a, b) => {
    // Ordenar para que los vehículos de tu agencia aparezcan al final (se rendericen encima)
    if (a.isUserAgencyVehicle && !b.isUserAgencyVehicle) return 1;
    if (!a.isUserAgencyVehicle && b.isUserAgencyVehicle) return -1;
    return 0;
  });

  // Debug: contar vehículos de tu agencia
  const userAgencyCount = vehicleMarkers.filter(v => v.isUserAgencyVehicle).length;
  //console.log(`🔴 Vehículos de tu agencia encontrados: ${userAgencyCount}/${vehicleMarkers.length}`);

  // Debug: mostrar vehículos de tu agencia específicamente
  const userAgencyVehicles = vehicleMarkers.filter(v => v.isUserAgencyVehicle);
  /*console.log('🔴 Vehículos de tu agencia:', userAgencyVehicles.map(v => ({
    id: v.id,
    title: v.title?.substring(0, 30),
    price: v.price,
    isUserAgencyVehicle: v.isUserAgencyVehicle,
    position: v.position
  })));*/

  // Derivar cluster activo (vehículos cercanos en score) si hay vehículo activo
  const activeVehicle = pinnedVehicle || hoveredVehicle;
  const activeCluster = activeVehicle
    ? vehicleMarkers.filter(v => Math.abs(v.score - activeVehicle.score) <= SCORE_THRESHOLD)
    : [];

  return (
    <div className="glass-effect rounded-xl p-4 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base lg:text-lg font-semibold text-white">Distribución de Precios</h3>
          <p className="text-xs text-gray-500 hidden sm:block">Tocá las marcas para ver detalles</p>
        </div>
        <span className="text-xs sm:text-sm text-gray-400">{stats.count} vehículos</span>
      </div>

      {/* Gráfico visual del boxplot */}
      <div className="h-16 mb-9 relative">
        {/* Barra de fondo */}
        <div className="absolute inset-0 bg-gray-700/30 rounded-lg"></div>

        {/* Segmentos del boxplot */}
        <div className="absolute inset-0 flex rounded-lg overflow-hidden">
          <div
            className="bg-blue-500/40 border-r border-blue-400/50"
            style={{ width: `${q1Width}%` }}
          ></div>
          <div
            className="bg-green-500/60 border-r border-green-400/50"
            style={{ width: `${medianWidth - q1Width}%` }}
          ></div>
          <div
            className="bg-yellow-500/60 border-r border-yellow-400/50"
            style={{ width: `${q3Width - medianWidth}%` }}
          ></div>
          <div
            className="bg-red-500/40"
            style={{ width: `${100 - q3Width}%` }}
          ></div>
        </div>

        {/* Marcadores de cuartiles */}
        <div className="absolute inset-0">
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-blue-400"
            style={{ left: `${q1Width}%` }}
          ></div>
          <div
            className="absolute top-0 bottom-0 w-1 bg-white"
            style={{ left: `${medianWidth}%` }}
          ></div>
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-orange-400"
            style={{ left: `${q3Width}%` }}
          ></div>
        </div>

        {/* Ticks individuales de vehículos */}
        <div className="absolute -bottom-1 left-0 right-0 h-2 z-20 overflow-visible">
          {vehicleMarkers.map((vehicle, index) => {
            // Debug para el vehículo específico
            /*if (vehicle.id === 'MLA2397631842') {
              console.log('🎯 Renderizando vehículo MLA2397631842:', {
                isUserAgencyVehicle: vehicle.isUserAgencyVehicle,
                position: vehicle.position,
                price: vehicle.price
              });
            }*/
            return (
              <div
                key={vehicle.id || index}
                className={`absolute cursor-pointer group ${vehicle.isUserAgencyVehicle ? 'z-30' : 'z-20'}`}
                style={{
                  left: `${Math.max(0, Math.min(100, vehicle.position))}%`,
                  transform: 'translateX(-50%)',
                  width: '12px',
                  height: '16px'
                }}
                onMouseEnter={() => setHoveredVehicle(vehicle)}
                onMouseLeave={() => setHoveredVehicle(null)}
                onClick={() => setPinnedVehicle(pinnedVehicle?.id === vehicle.id ? null : vehicle)}
              >
                {/* Icono rojo para vehículos de la agencia del usuario */}
                {vehicle.isUserAgencyVehicle && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-40">
                    <div className="text-red-400 text-lg  drop-shadow-lg">⭐</div>
                  </div>
                )}

                {/* Línea del tick */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                  <div className={`w-px h-3 transition-all duration-150 ${vehicle.isUserAgencyVehicle
                    ? 'bg-red-400 group-hover:bg-red-300 group-hover:h-4'
                    : 'bg-gray-300 group-hover:bg-cyan-400 group-hover:h-4'
                    }`}></div>
                </div>

                {/* Punto visible (ayuda a no "perder" el tick) */}
                <div className={`absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full transition-colors ${vehicle.isUserAgencyVehicle
                  ? 'bg-red-400 group-hover:bg-red-300'
                  : 'bg-gray-300 group-hover:bg-cyan-400'
                  }`}></div>
              </div>
            )
          }
          )}
        </div>

        {/* Etiquetas de precios */}
        <div className="absolute -bottom-7 left-0 right-0 flex justify-between text-[10px] lg:text-xs text-gray-400">
          <span>{formatPrice(stats.min)}</span>
          <span className="hidden sm:inline">{formatPrice(stats.q1)}</span>
          <span>{formatPrice(stats.median)}</span>
          <span className="hidden sm:inline">{formatPrice(stats.q3)}</span>
          <span>{formatPrice(stats.max)}</span>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-3 text-xs lg:text-sm">
        <div className="text-center">
          <p className="text-gray-400">Mín</p>
          <p className="font-semibold text-blue-400">{formatPrice(stats.min)}</p>
        </div>
        <div className="text-center">
          <p className="text-gray-400">Q1</p>
          <p className="font-semibold text-green-400">{formatPrice(stats.q1)}</p>
        </div>
        <div className="text-center">
          <p className="text-gray-400">Mediana</p>
          <p className="font-semibold text-yellow-400">{formatPrice(stats.median)}</p>
        </div>
        <div className="text-center hidden lg:block">
          <p className="text-gray-400">Q3</p>
          <p className="font-semibold text-orange-400">{formatPrice(stats.q3)}</p>
        </div>
        <div className="text-center hidden lg:block">
          <p className="text-gray-400">Máximo</p>
          <p className="font-semibold text-red-400">{formatPrice(stats.max)}</p>
        </div>
      </div>

      {/* Información adicional */}
      <div className="mt-4 pt-4 border-t border-[#2a2926]">
        <div className="flex justify-between text-xs text-gray-400">
          <span>Promedio: <span className="text-white font-medium">{formatPrice(stats.mean)}</span></span>
          <span>Rango IQR: <span className="text-white font-medium">{formatPrice(stats.iqr)}</span></span>
        </div>
      </div>

      {/* Información del vehículo - hover o fijado */}
      {(hoveredVehicle || pinnedVehicle) && (
        <div className={`mt-6 p-4 rounded-lg relative ${(pinnedVehicle || hoveredVehicle)?.isUserAgencyVehicle
          ? 'bg-red-900/20 border-2 border-red-400 shadow-lg shadow-red-400/20'
          : 'bg-[#232321]/30 border border-gray-600'
          }`}>
          {pinnedVehicle && (
            <button
              onClick={() => setPinnedVehicle(null)}
              className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Si hay un cluster de vehículos cercanos, mostrar todos */}
          {activeCluster && activeCluster.length > 1 ? (
            <div className="space-y-4">
              <div className="text-xs text-gray-400">Vehículos cercanos (±{Math.round(SCORE_THRESHOLD * 100)}% del rango normalizado)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeCluster.map((veh) => (
                  <div key={veh.id} className={`flex gap-3 p-3 rounded-lg border transition-colors ${veh.isUserAgencyVehicle
                    ? 'border-red-400 bg-red-400/10'
                    : 'border-gray-700 hover:border-[#48a877]'
                    }`}>
                    <div className="flex-shrink-0">
                      {veh.pictures ? (
                        <img
                          src={veh.pictures}
                          alt={veh.title}
                          className="w-28 h-20 object-cover rounded-md border border-gray-600 cursor-pointer hover:border-blue-400"
                          onClick={() => setExpandedImage(veh.pictures)}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-28 h-20 bg-gray-700 rounded-md border border-gray-600 items-center justify-center ${veh.pictures ? 'hidden' : 'flex'}`}
                      >
                        <span className="text-gray-500 text-xs">Sin imagen</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-semibold text-white text-sm leading-snug truncate flex items-center gap-2">
                          {veh.title}
                          {veh.isUserAgencyVehicle && <span className="text-red-400">⭐</span>}
                        </h4>
                        <button className="text-xs text-[#48a877] hover:text-[#48a877]" onClick={() => setPinnedVehicle(veh)}>Fijar</button>
                      </div>
                      <div className="mt-1 text-xs text-gray-400">
                        <div>Precio: <span className="text-green-400 font-semibold">{formatPrice(veh.price)}</span></div>
                        {veh.year && <div>Año: <span className="text-white">{veh.year}</span></div>}
                        {veh.kilometers && <div>Kms: <span className="text-white">{veh.kilometers}</span></div>}
                        {veh.city && <div>Ubicación: <span className="text-white">{veh.city}</span></div>}
                        {veh.days_old && <div>Antiguedad: <span className="text-white">{veh.days_old} días</span></div>}
                      </div>
                      {veh.permalink && (
                        <div className="mt-2">
                          <a
                            href={veh.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#48a877] hover:text-[#48a877] text-xs inline-flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Ver en MercadoLibre
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Caso original: un solo vehículo
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Imagen del vehículo */}
              <div className="flex-shrink-0">
                {(pinnedVehicle || hoveredVehicle).pictures ? (
                  <img
                    src={(pinnedVehicle || hoveredVehicle).pictures}
                    alt={(pinnedVehicle || hoveredVehicle).title}
                    className="w-full sm:w-40 h-32 sm:h-30 object-cover rounded-lg border border-gray-600 cursor-pointer hover:border-blue-400 transition-colors"
                    onClick={() => setExpandedImage((pinnedVehicle || hoveredVehicle).pictures)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`w-full sm:w-40 h-32 sm:h-30 bg-gray-700 rounded-lg border border-gray-600 flex items-center justify-center ${(pinnedVehicle || hoveredVehicle).pictures ? 'hidden' : 'flex'}`}
                >
                  <span className="text-gray-500 text-xs">Sin imagen</span>
                </div>
              </div>

              {/* Información del vehículo */}
              <div className="flex-1">
                <h4 className="font-semibold text-white mb-3 pr-6 flex items-center gap-2">
                  {(pinnedVehicle || hoveredVehicle).title}
                  {(pinnedVehicle || hoveredVehicle).isUserAgencyVehicle && (
                    <span className="text-red-400">⭐</span>
                  )}
                </h4>

                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-gray-400 block">Precio:</span>
                    <span className="text-green-400 font-semibold text-sm">
                      {formatPrice((pinnedVehicle || hoveredVehicle).price)}
                    </span>
                  </div>
                  {(pinnedVehicle || hoveredVehicle).year && (
                    <div>
                      <span className="text-gray-400 block">Año:</span>
                      <span className="text-white font-medium">{(pinnedVehicle || hoveredVehicle).year}</span>
                    </div>
                  )}
                  {(pinnedVehicle || hoveredVehicle).kilometers && (
                    <div>
                      <span className="text-gray-400 block">Kilómetros:</span>
                      <span className="text-white font-medium">
                        {(pinnedVehicle || hoveredVehicle).kilometers.toLocaleString()} km
                      </span>
                    </div>
                  )}
                  {(pinnedVehicle || hoveredVehicle).city && (
                    <div>
                      <span className="text-gray-400 block">Ubicación:</span>
                      <span className="text-white font-medium">{(pinnedVehicle || hoveredVehicle).city}</span>
                    </div>
                  )}
                </div>

                {(pinnedVehicle || hoveredVehicle).permalink && (
                  <div className="mt-3 pt-3 border-t border-gray-600">
                    <a
                      href={(pinnedVehicle || hoveredVehicle).permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#48a877] hover:text-[#48a877] text-xs inline-flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Ver en MercadoLibre
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {!pinnedVehicle && (
            <div className="mt-2 text-xs text-gray-500 text-center">
              Haz click en el punto para fijar esta información
            </div>
          )}
        </div>
      )}

      {/* Modal de imagen expandida */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-6xl max-h-[90vh] w-full h-full">
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={expandedImage}
              alt="Imagen expandida del vehículo"
              className="w-full h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceBoxplot;